import React, { useState, useEffect, useRef } from 'react';
import { IoPlayOutline, IoPauseOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import { ImSpinner2 } from 'react-icons/im';
import { PiPlaylistBold } from "react-icons/pi";
import { RiPlayList2Line } from "react-icons/ri";
import { LuClock3 } from "react-icons/lu";
import { Previous, Next } from 'iconsax-react';
import { useAudio } from '../../utils/AudioContext';

interface CardProp {
    img: string;
    audioSrc: string | string[];
    title: string;
    isPlaylistOpen: boolean;
    togglePlaylist: () => void;
}

const formatTime = (time: number) => {
    if (isNaN(time)) return "00:00";
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    const formattedHours = hours > 0 ? `${hours}:` : '';
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

    return `${formattedHours}${formattedMinutes}:${formattedSeconds}`;
};

const AudioCard: React.FC<CardProp> = ({ img, audioSrc, title, isPlaylistOpen, togglePlaylist }) => {
    const {
        img: globalImg,
        title: globalTitle,
        isPlaying: globalIsPlaying,
        progress: globalProgress,
        duration: globalDuration,
        currentTime: globalCurrentTime,
        currentTrackIndex: globalTrackIndex,
        playAudio: globalPlayAudio,
        pauseAudio: globalPauseAudio,
        togglePlayPause: globalTogglePlayPause,
        playNextTrack: globalPlayNextTrack,
        playPreviousTrack: globalPlayPreviousTrack,
        handleScrub: globalHandleScrub,
    } = useAudio();

    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [trackDurations, setTrackDurations] = useState<number[]>([]);
    const audioRefs = useRef<HTMLAudioElement[]>([]);

    const isSeries = Array.isArray(audioSrc);

    // Initialize audio elements and load durations
    useEffect(() => {
        if (isSeries) {
            const loadDurations = async () => {
                const durations = await Promise.all(
                    (audioSrc as string[]).map(async (src) => {
                        return new Promise<number>((resolve) => {
                            const audio = new Audio(src);
                            audio.addEventListener('loadedmetadata', () => {
                                resolve(audio.duration);
                            });
                            audio.addEventListener('error', () => {
                                resolve(0); // Fallback if metadata can't be loaded
                            });
                        });
                    })
                );
                setTrackDurations(durations);
            };

            loadDurations();
        }
    }, [audioSrc, isSeries]);

    // Update local state when global state changes
    useEffect(() => {
        if (globalImg === img && globalTitle === title) {
            setIsPlaying(globalIsPlaying);
            setProgress(globalProgress);
            setDuration(globalDuration);
            setCurrentTime(globalCurrentTime);
            setCurrentTrackIndex(globalTrackIndex);
        } else {
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime(0);
        }
    }, [globalImg, globalTitle, globalIsPlaying, globalProgress, globalDuration, globalCurrentTime, globalTrackIndex, img, title]);

    const handleDownloadClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (!audioSrc) {
            e.preventDefault();
            return;
        }
        setLoading(true);
        setTimeout(() => setLoading(false), 2000);
    };

    const handlePlayClick = () => {
        if (isSeries) {
            globalPlayAudio(img, title, audioSrc, currentTrackIndex);
        } else {
            globalPlayAudio(img, title, audioSrc, 0);
        }
    };

    const handleTrackPlay = (index: number) => {
        if (isSeries) {
            globalPlayAudio(img, title, audioSrc, index);
        }
    };

    const handleTogglePlayPause = () => {
        if (globalImg === img && globalTitle === title) {
            globalTogglePlayPause();
        } else {
            handlePlayClick();
        }
    };

    const handlePreviousTrack = () => {
        if (currentTrackIndex > 0) {
            globalPlayAudio(img, title, audioSrc, currentTrackIndex - 1);
        }
    };

    const handleNextTrack = () => {
        if (isSeries && currentTrackIndex < audioSrc.length - 1) {
            globalPlayAudio(img, title, audioSrc, currentTrackIndex + 1);
        }
    };

    const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (globalImg === img && globalTitle === title) {
            globalHandleScrub(e);
        }
    };

    const getTrackDuration = (index: number) => {
        if (isSeries && trackDurations.length > index) {
            return trackDurations[index];
        }
        return duration;
    };

    return (
        <div className="flex flex-col items-center gap-y-5">
            <img src={img} alt={title} className="xl:w-[300px] lg:w-60 w-52 rounded-3xl" />
            <h1 className="font-poppins text-white text-xl w-64 text-center font-light min-h-16">{title}</h1>

            {isSeries ? (
                <div className="w-full">
                    <div className="flex flex-col gap-x-5 items-center">
                        <button
                            onClick={togglePlaylist}
                            disabled={!audioSrc}
                            className={`bg-greenV px-12 py-4 rounded-md cursor-pointer flex items-center ${!audioSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={isPlaying ? "Pause Audio" : "Play Audio"}
                        >
                            <PiPlaylistBold />
                        </button>
                        {isPlaylistOpen && (
                            <div className="w-full text-white mt-7 albumBg px-5 py-6 font-poppins">
                                <div className="flex items-center px-6">
                                    <div className="flex items-center gap-x-2 text-lg">
                                        <span>List</span>
                                        <RiPlayList2Line />
                                    </div>
                                    <LuClock3 className="ml-16" />
                                </div>

                                <div className="flex flex-col text-sm mt-4 gap-y-5">
                                    {Array.isArray(audioSrc) && audioSrc.map((src, index) => (
                                        <div
                                            key={index}
                                            className="flex justify-between bg-lightGray px-6 py-2 font-light rounded-md cursor-pointer"
                                            onClick={() => handleTrackPlay(index)}
                                        >
                                            <div className="flex items-center gap-2 ">
                                                <span>{`Track ${index + 1}`}</span>
                                            </div>
                                            <div className="flex gap-x-2">
                                                {formatTime(getTrackDuration(index))}
                                                <a
                                                    href={src}
                                                    download = {true}
                                                    onClick={handleDownloadClick}
                                                    className={`text-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {loading ? (
                                                        <ImSpinner2 className="animate-spin" />
                                                    ) : (
                                                        <MdOutlineFileDownload />
                                                    )}
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Playback Control Menu */}
                                <div className="flex justify-center items-center mt-5">
                                    <button
                                        onClick={handlePreviousTrack}
                                        disabled={currentTrackIndex === 0}
                                        className="px-4 py-2 rounded-md "
                                        title="Previous Track"
                                    >
                                        <Previous size={18} />
                                    </button>
                                    <button
                                        onClick={handleTogglePlayPause}
                                        className="px-4 py-2 rounded-md"
                                        title={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? <IoPauseOutline /> : <IoPlayOutline />}
                                    </button>
                                    <button
                                        onClick={handleNextTrack}
                                        disabled={currentTrackIndex === (Array.isArray(audioSrc) ? audioSrc.length - 1 : 0)}
                                        className="px-4 py-2 rounded-md"
                                        title="Next Track"
                                    >
                                        <Next size={18} />
                                    </button>
                                </div>

                                {/* Progress Bar */}
                                <input
                                    type="range"
                                    value={progress}
                                    onChange={handleScrub}
                                    className="audio-progress w-full mt-3"
                                    min="0"
                                    max="100"
                                />
                                <div className="text-white mt-1 text-xs">
                                    {`${formatTime(currentTime)} / ${formatTime(duration)}`}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="flex gap-x-5 items-center">
                        <button
                            onClick={handleTogglePlayPause}
                            disabled={!audioSrc}
                            className={`bg-greenV px-12 py-4 rounded-md cursor-pointer flex items-center ${!audioSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={isPlaying ? "Pause Audio" : "Play Audio"}
                        >
                            {isPlaying ? <IoPauseOutline /> : <IoPlayOutline />}
                        </button>

                        <a
                            href={audioSrc as string}
                            download = {true}
                            onClick={handleDownloadClick}
                            className={`bg-greenV px-12 py-4 rounded-md cursor-pointer flex items-center ${!audioSrc ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <ImSpinner2 className="animate-spin" />
                            ) : (
                                <MdOutlineFileDownload />
                            )}
                        </a>
                    </div>

                    {isPlaying && (
                        <>
                            <input
                                type="range"
                                value={progress}
                                onChange={handleScrub}
                                className="audio-progress w-full mt-3"
                                min="0"
                                max="100"
                            />
                            <div className="text-white mt-1">
                                {`${formatTime(currentTime)} / ${formatTime(duration)}`}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioCard;